// apps/share-web/src/player-view/version-picker.ts
//
// «Version ▾» — выбор записи прямо в шапке плеера (единый сетлист, фаза 3,
// design gate G2). Три состояния по длине списка, а не одно:
//   0 версий  — ничего не рисуется (плеер открыт по прямой ссылке на shareId,
//               версий у песни в этом сетлисте нет вовсе);
//   1 версия  — просто строка меты «Label · дата», никакого контрола: раскрывать
//               нечего, а кнопка с одним вариантом внутри — фальшивый выбор;
//   ≥2 версии — полный пикер: на широком экране поповер под триггером, на
//               телефоне — нижний лист (DS-пробел, см. шапку version-picker.css).
//
// DS-ПРОБЕЛ: `bw-dropdown` не умеет двухстрочные пункты (подпись + мета) и тег
// «newest» рядом с ними — его `options: {value,label,color?}[]` несёт только
// одну строку текста и точку-цвет (packages/ui-components/src/bw-dropdown.ts).
// Вместо доработки самого компонента ради одного потребителя (риск задеть все
// остальные dropdown'ы приложения) — композиция: `bw-button` как триггер + свой
// шеврон, и свой поповер/лист с классами `vp-*`, визуально повторяющими рецепт
// `bw-dropdown.css` (утопленная панель, engraved-строки), но не зависящими от
// его закрытой Lit-разметки. Если вариант с мета-строкой понадобится ещё где-то
// — это готовый кандидат на `BwDropdownOption.meta?`/`tag?` в самом компоненте.
//
// Плеер живёт и в телефонном, и в широком режиме БЕЗ перезагрузки (курс на
// расширение), поэтому режим — ЖИВОЙ (watchPhoneMode, реагирует на переход
// порога), а не снимок на монтаже — тот же урок, что и у блока чарта
// (TD-260828-chart-phone-mode-snapshotted-at-mount, phone-mode.ts).
//
// ТРИГГЕР — зеркало desktop `.sa-takes-btn` (app/renderer/ui/song-actions.js
// ~30-55, `.song-actions` group): иконка «versions» + подпись выбранной
// версии + « / N» счётчиком, БЕЗ тега «newest» на самом триггере — этот тег
// живёт только на пункте списка (buildOption), потому что он описывает ОДИН
// конкретный пункт («это самая новая запись»), а не то, что сейчас открыто
// (критика живой приёмки, product owner: «newest marker stays inside the menu
// items, not on the trigger»). Тем же поводом триггер стоит в ПРАВОЙ группе
// шапки плеера (mount.ts `.pv-song-actions`, рядом с кнопкой Chart), а не под
// заголовком — там же, где desktop держит `.song-actions`.
import { t, getLocale } from '../ui/i18n.js';
import { icon } from '../ui/icons.js';
import { formatShortDate } from '../ui/format-date.js';
import { watchPhoneMode as watchPhoneModeReal, type PhoneModeWatch } from './phone-mode.js';
import '@bandway/ui-components';
import '@bandway/ui-components/styles/bw-button.css';
import '@bandway/ui-components/styles/bw-tag.css';
import '../styles/version-picker.css';

export interface VersionPickerItem {
  shareId: string;
  sessionId: string | null;
  /** null — запись без ярлыка (импорт без сессии); показывается запасным словом. */
  label: string | null;
  recordedAt: string | null;
  /** `versions[0]` этого списка — самая новая запись. */
  isNewest: boolean;
  /** Версия, открытая СЕЙЧАС в плеере (личный выбор или дефолт newest). */
  isCurrent: boolean;
}

export interface VersionPickerVm {
  /** Newest-first, ровно как `SetlistSong.versions`/`ScreenRow.versions`. */
  items: VersionPickerItem[];
}

export interface VersionPickerDeps {
  onSelect: (item: VersionPickerItem) => void;
  /** Шов для тестов — настоящий watchPhoneMode в проде. */
  watchPhoneMode?: (onChange: (phone: boolean) => void) => PhoneModeWatch;
}

export interface VersionPickerHandle {
  el: HTMLElement;
  unmount(): void;
}

// «Стопка записей» — та же роль, что desktop `ICON.versions`
// (app/renderer/ui/icons.js `svgIcon('versions', 16)`): три сложенных
// прямоугольника. Скопирован дословно (не через ui/icons.ts — тот навешивает
// общий `fill="none" stroke="currentColor"` на обёртку, а эти пути несут СВОЮ
// заливку `fill="currentColor"` и обводки не хотят вовсе, тем же приёмом, что
// PENCIL_SVG/CHECK_SVG в song-actions.js), — PRODUCT.md принцип 7: «one
// signal keeps one meaning across every surface».
const VERSIONS_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<path d="M6.28693 3.54525C8.33297 3.41612 10.7144 3.56704 12.7841 3.57294C13.4707 3.5749 14.401 4.38623 14.4454 5.08661C14.5303 6.42784 14.4962 7.83294 14.4966 9.19167L14.4953 12.2822C14.4934 13.8611 14.7568 15.5269 12.6824 15.8281C10.5978 15.8907 8.40958 15.8302 6.31959 15.8064C6.1021 15.8039 5.57992 15.6455 5.39035 15.5231C4.4322 14.9042 4.53969 13.8885 4.54158 12.8987C4.54164 12.0303 4.54008 11.2119 4.5398 10.3712L4.54136 7.27005C4.54392 5.61996 4.24876 4.0154 6.28693 3.54525Z" fill="currentColor"/>' +
  '<path d="M15.5817 6.30078C16.736 6.42133 17.0033 7.23423 17.0059 8.24948C17.0158 10.8177 17.0125 13.3888 17.0081 15.9567C17.0071 16.5663 17.0022 16.958 16.65 17.4741C16.3604 17.8985 15.9103 18.1273 15.4105 18.1858C14.6345 18.2766 13.7633 18.2402 12.9828 18.2398L8.51653 18.2417C7.96549 18.161 7.27007 17.776 7.16748 17.1881C7.20214 17.0739 7.26539 16.9322 7.40514 16.926C9.40152 16.8377 11.4234 16.97 13.424 16.8813C13.5698 16.8749 13.8431 16.8393 13.9719 16.7964C15.966 16.1318 15.5127 14.2023 15.5629 12.5723C15.6279 10.4831 15.4608 8.34428 15.5817 6.30078Z" fill="currentColor"/>' +
  '<path d="M18.108 8.7998C19.3022 8.87772 19.4558 9.71943 19.4622 10.7156L19.4658 16.317C19.4654 16.8508 19.4961 18.5211 19.4163 18.9308C19.3403 19.3113 19.151 19.6601 18.8733 19.931C18.1124 20.6829 16.3239 20.484 15.3011 20.4836L10.575 20.4777C10.1786 20.4876 9.90555 20.48 9.60671 20.1946C9.4279 20.0239 9.2702 19.8048 9.27249 19.5482C9.27288 19.506 9.31266 19.4249 9.34019 19.3924C9.53427 19.1623 15.4909 19.4472 16.4762 19.2084C16.8346 19.1216 17.1704 18.9252 17.4334 18.6682C17.738 18.3749 17.9459 17.9955 18.0292 17.5809C18.1523 16.9707 18.1093 15.195 18.1087 14.4949L18.108 8.7998Z" fill="currentColor"/>' +
  '</svg>';

function displayLabel(item: VersionPickerItem): string {
  const label = (item.label ?? '').trim();
  return label || t('versionPicker.unlabeled');
}

function displayDate(iso: string | null): string {
  return iso ? formatShortDate(iso, getLocale()) : '';
}

/** Один пункт списка — общий для поповера и нижнего листа: одно и то же
 *  содержимое, разная упаковка вокруг (см. шапку модуля). */
function buildOption(item: VersionPickerItem, onPick: (item: VersionPickerItem) => void): HTMLButtonElement {
  const opt = document.createElement('button');
  opt.type = 'button';
  opt.className = 'vp-option';
  opt.dataset.role = 'version-picker-option';
  opt.dataset.shareId = item.shareId;
  opt.setAttribute('role', 'option');
  opt.setAttribute('aria-selected', String(item.isCurrent));

  const check = document.createElement('span');
  check.className = 'vp-option-check';
  check.setAttribute('aria-hidden', 'true');
  check.textContent = item.isCurrent ? '✓' : '';
  opt.appendChild(check);

  const body = document.createElement('span');
  body.className = 'vp-option-body';
  const label = document.createElement('span');
  label.className = 'vp-option-label';
  label.textContent = displayLabel(item);
  body.appendChild(label);
  const meta = document.createElement('span');
  meta.className = 'vp-option-meta';
  meta.textContent = displayDate(item.recordedAt);
  body.appendChild(meta);
  opt.appendChild(body);

  if (item.isNewest) {
    const tag = document.createElement('bw-tag');
    tag.setAttribute('size', 'sm');
    tag.className = 'vp-option-tag';
    tag.textContent = t('versionPicker.newest');
    opt.appendChild(tag);
  }

  opt.addEventListener('click', () => onPick(item));
  return opt;
}

function buildOptionsList(items: VersionPickerItem[], onPick: (item: VersionPickerItem) => void): HTMLElement {
  const list = document.createElement('div');
  list.className = 'vp-list';
  list.dataset.role = 'version-picker-list';
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', t('versionPicker.ariaLabel'));
  for (const item of items) list.appendChild(buildOption(item, onPick));

  const footnote = document.createElement('div');
  footnote.className = 'vp-footnote';
  footnote.textContent = t('versionPicker.footnote');
  list.appendChild(footnote);

  return list;
}

/**
 * Триггер — зеркало desktop `.sa-takes-btn` (song-actions.js): иконка
 * «versions» в icon-start, подпись выбранной версии, « / N» счётчиком —
 * `size="md"`, та же высота, что у чипов key/BPM самого `bw-song-header`
 * (тоже `size="md"`) и у соседней кнопки Chart (`.pv-song-actions`,
 * mount.ts). Без тега «newest» на себе — см. шапку модуля.
 */
function buildTrigger(current: VersionPickerItem, total: number, popoverId: string): HTMLElement {
  const btn = document.createElement('bw-button');
  btn.setAttribute('size', 'md');
  btn.className = 'vp-trigger';
  btn.dataset.role = 'version-picker-trigger';
  btn.setAttribute('aria-haspopup', 'listbox');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-controls', popoverId);
  // Видимая подпись — только «Take 2 / 3»; полное «Version: …» остаётся
  // доступным именем для скринридера, читающего иконку без контекста ряда.
  btn.setAttribute('aria-label', t('versionPicker.trigger', { label: displayLabel(current) }));

  const iconStart = document.createElement('span');
  iconStart.setAttribute('slot', 'icon-start');
  iconStart.className = 'vp-trigger-icon';
  iconStart.innerHTML = VERSIONS_ICON;
  btn.appendChild(iconStart);

  const label = document.createElement('span');
  label.className = 'vp-trigger-label';
  label.textContent = displayLabel(current);
  btn.appendChild(label);

  const count = document.createElement('span');
  count.className = 'vp-trigger-count';
  count.textContent = '/ ' + String(total);
  btn.appendChild(count);

  const chev = document.createElement('span');
  chev.setAttribute('slot', 'icon-end');
  chev.className = 'vp-chevron';
  chev.innerHTML = icon('chevronDown');
  btn.appendChild(chev);

  return btn;
}

/**
 * Нижний лист телефона — DS-пробел, см. шапку version-picker.css («DS
 * proposal: bw-sheet»). Монтируется в `body`, тем же приёмом, что и слой чтения
 * (`fullscreen-slot.ts`): контекст наложения `.app` иначе прижал бы z-index
 * листа к уровню самого плеера.
 *
 * `onClosed` зовётся РОВНО один раз, кем бы лист ни был закрыт (скрим, Escape,
 * выбор пункта, размонтаж владельца) — так у владельца всегда один источник
 * правды «лист открыт?», а не три разных пути забыть его сбросить.
 */
function mountSheet(
  items: VersionPickerItem[],
  onPick: (item: VersionPickerItem) => void,
  returnFocusTo: HTMLElement,
  onClosed: () => void,
): { close(): void } {
  const scrim = document.createElement('div');
  scrim.className = 'vp-scrim';
  scrim.dataset.role = 'version-picker-scrim';

  const sheet = document.createElement('div');
  sheet.className = 'vp-sheet';
  sheet.dataset.role = 'version-picker-sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-label', t('versionPicker.sheetTitle'));

  const handle = document.createElement('div');
  handle.className = 'vp-sheet-handle';
  handle.setAttribute('aria-hidden', 'true');
  sheet.appendChild(handle);

  const title = document.createElement('div');
  title.className = 'vp-sheet-title';
  title.textContent = t('versionPicker.sheetTitle');
  sheet.appendChild(title);

  const list = buildOptionsList(items, (item) => onPick(item));
  list.classList.add('vp-sheet-list');
  sheet.appendChild(list);

  document.body.appendChild(scrim);
  document.body.appendChild(sheet);

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey);
    scrim.remove();
    sheet.remove();
    onClosed();
    returnFocusTo.focus();
  };

  scrim.addEventListener('click', close);

  const focusables = (): HTMLElement[] =>
    Array.from(sheet.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])'));

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    const items_ = focusables();
    if (items_.length === 0) return;
    const first = items_[0]!;
    const last = items_[items_.length - 1]!;
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };
  document.addEventListener('keydown', onKey);

  // Фокус — на первый пункт: лист открывается и с клавиатуры/скринридером, не
  // только тапом. `.is-open` — на следующем кадре, чтобы CSS-переход отыграл
  // ОТ исходного состояния (появление сразу в конечном — не анимация, а щелчок).
  requestAnimationFrame(() => {
    scrim.classList.add('is-open');
    sheet.classList.add('is-open');
    focusables()[0]?.focus();
  });

  return { close };
}

/** Выбор версии песни. Ничего не рисует при 0 версий, простую строку меты при
 *  1 — полный контрол только когда правда есть из чего выбирать. */
export function mountVersionPicker(host: HTMLElement, vm: VersionPickerVm, deps: VersionPickerDeps): VersionPickerHandle {
  const root = document.createElement('div');
  root.className = 'vp-root';
  root.dataset.role = 'version-picker';
  host.appendChild(root);

  const items = vm.items;

  if (items.length === 0) {
    return { el: root, unmount: () => { root.remove(); } };
  }

  const current = items.find((i) => i.isCurrent) ?? items[0]!;

  if (items.length === 1) {
    const meta = document.createElement('div');
    meta.className = 'vp-single';
    meta.dataset.role = 'version-picker-single';
    meta.textContent = t('versionPicker.singleMeta', { label: displayLabel(current), date: displayDate(current.recordedAt) });
    root.appendChild(meta);
    return { el: root, unmount: () => { root.remove(); } };
  }

  // ≥2 версии — полный пикер.
  const popoverId = `vp-popover-${Math.random().toString(36).slice(2)}`;
  const triggerBtn = buildTrigger(current, items.length, popoverId);
  root.appendChild(triggerBtn);

  // Поповер — ПОРТАЛ в body, `position: fixed`, координаты считаются от
  // триггера в момент открытия. НЕ статичный ребёнок `.vp-root` со
  // `position: absolute; top: 100%`: `.vp-root` живёт внутри `.pv-songhead` →
  // `.pv-root`, а `.pv-root` несёт `overflow-y: auto` (player-view.css, ради
  // вертикальной прокрутки самого плеера) — абсолютно спозиционированный
  // потомок скроллящегося предка обрезается его границами кадрирования, и
  // поповер под триггером в шапке плеера частично утонул бы под низом экрана.
  // Критика живой приёмки (16073e7c, пункт 3). `position: fixed` считает
  // координаты от вьюпорта и обрезки не знает вовсе — тот же приём, что уже
  // несёт нижний лист (mountSheet ниже) и полноэкранный слой чтения
  // (fullscreen-slot.ts).
  //
  // Выравнивание — по ПРАВОМУ краю триггера (`right`, не `left`): триггер
  // теперь стоит в правой группе шапки (`.pv-song-actions`, mount.ts), у
  // самого края поверхности, — поповер, растущий от левого края триггера
  // вправо, вылезал бы за край экрана (критика product owner, живая приёмка
  // c1b5fae0, пункт 1: «Popover opens under the trigger aligned to its RIGHT
  // edge»).
  const popover = document.createElement('div');
  popover.className = 'vp-popover';
  popover.id = popoverId;
  popover.hidden = true;
  popover.appendChild(buildOptionsList(items, (item) => pick(item)));

  let popoverOpen = false;
  let sheetOpen = false;
  let closeSheetFn: (() => void) | null = null;

  /** Клик вне и триггера, И самого поповера — тот вне двух узлов, между
   *  которыми поповер портирован (root → body), а не один. */
  const onDocPointer = (e: PointerEvent): void => {
    const target = e.target as Node;
    if (root.contains(target) || popover.contains(target)) return;
    closePopover();
  };
  const onDocKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); closePopover(); (triggerBtn as HTMLElement).focus?.(); }
  };
  /** Поповер закрывается на скролле/ресайзе, а не следует за триггером —
   *  простейшее решение без ре-позиционирования на каждый кадр скролла
   *  (тот же компромисс, что у большинства popover-реализаций). */
  const onScrollOrResize = (): void => { closePopover(); };

  function openPopover(): void {
    if (popoverOpen) return;
    popoverOpen = true;
    const rect = (triggerBtn as HTMLElement).getBoundingClientRect();
    popover.style.top = `${rect.bottom + 6}px`;
    // Правый край поповера = правый край триггера (`right`, координата от
    // правого края вьюпорта) — не `left`: триггер сидит у правого края шапки
    // (`.pv-song-actions`), и поповер шире триггера растекался бы вправо за
    // экран, будь он приколот по левому краю.
    popover.style.left = 'auto';
    popover.style.right = `${window.innerWidth - rect.right}px`;
    document.body.appendChild(popover);
    popover.hidden = false;
    triggerBtn.setAttribute('aria-expanded', 'true');
    document.addEventListener('pointerdown', onDocPointer);
    document.addEventListener('keydown', onDocKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
  }
  function closePopover(): void {
    if (!popoverOpen) return;
    popoverOpen = false;
    popover.hidden = true;
    if (popover.parentNode) popover.parentNode.removeChild(popover);
    triggerBtn.setAttribute('aria-expanded', 'false');
    document.removeEventListener('pointerdown', onDocPointer);
    document.removeEventListener('keydown', onDocKey);
    window.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
  }

  function openSheet(): void {
    if (sheetOpen) return;
    sheetOpen = true;
    triggerBtn.setAttribute('aria-expanded', 'true');
    const { close } = mountSheet(items, (item) => pick(item), triggerBtn as HTMLElement, () => {
      sheetOpen = false;
      triggerBtn.setAttribute('aria-expanded', 'false');
    });
    closeSheetFn = close;
  }
  function closeSheet(): void {
    if (!sheetOpen) return;
    closeSheetFn?.();
    closeSheetFn = null;
  }

  function pick(item: VersionPickerItem): void {
    closePopover();
    closeSheet();
    deps.onSelect(item);
  }

  let phoneNow = false;
  const onTriggerClick = (): void => {
    if (phoneNow) { if (sheetOpen) closeSheet(); else openSheet(); }
    else { if (popoverOpen) closePopover(); else openPopover(); }
  };
  triggerBtn.addEventListener('bw-click', onTriggerClick);

  const watch = deps.watchPhoneMode ?? watchPhoneModeReal;
  const watcher = watch((phone) => {
    phoneNow = phone;
    // Переход через порог, пока что-то открыто, — закрываем оба: смешанное
    // состояние (поповер на телефоне, лист на десктопе) не бывает законным.
    closePopover();
    closeSheet();
  });
  phoneNow = watcher.isPhone();

  return {
    el: root,
    unmount: () => {
      closePopover();
      closeSheet();
      watcher.stop();
      root.remove();
    },
  };
}
